<script>
  import { onMount, tick } from "svelte"
  import Header from "./components/Header.svelte"
  import Commands from "./components/Commands.svelte"
  import Buttons from "./components/Buttons.svelte"
  import ChangeLog from "./components/ChangeLog.svelte"
  import Footer from "./components/Footer.svelte"
  import { getColor, getFont } from "./utility"
  import { commandsData } from "./assets/data"
  import colors from "./assets/colors"

  let canvas, ctx, gif, intervalInstance
  let heightOffset = 20
  let isLoading = false
  let loadingElem = "simulate"
  let dots = ""
  let settings = {
    width: {
      value: 700,
      default: 700
    },
    height: {
      value: 300,
      default: 300
    },
    fontFamily: {
      value: "system-ui"
    },
    fontSize: {
      value: 15,
      default: 15
    },
    typeWaitMs: {
      value: 50,
      default: 50
    }
  }
  $: width = parseInt(settings.width.value) > 100 ? settings.width.value : settings.width.default
  $: height = parseInt(settings.height.value) > 100 ? settings.height.value : settings.height.default
  $: fontFamily = settings.fontFamily.value
  $: fontSize = parseInt(settings.fontSize.value) > 0 ? settings.fontSize.value : settings.fontSize.default
  $: typeWaitMs = parseInt(settings.typeWaitMs.value) >= 0 ? settings.typeWaitMs.value : settings.typeWaitMs.default

  onMount(async () => {
    canvas = document.querySelector("canvas")
    ctx = canvas.getContext("2d")
    ctx.font = getFont(fontSize, fontFamily)

    fillHeader()

    document.onkeydown = (e) => {
      if (e.ctrlKey && e.key == "s") {
        e.preventDefault()
        startAnimation()
      }
    }
  })

  async function resetTerminal() {
    settings = { ...settings }
    ctx.clearRect(0, 0, width, height)
    await tick()
    fillHeader()
  }

  async function renderCommandInCanvas(command, isNewLine) {
    let charWidth = 0
    const lineWidth = ctx.measureText(command.path + "$").width + (command.path ? 30 : 12)

    ctx.fillStyle = getColor(colors.command)
    if (command.shouldType) {
      for (let i = 0; i < command.line.length; i++) {
        const letter = command.line[i]
        ctx.fillText(letter, lineWidth + charWidth, height - heightOffset)
        await new Promise((r) => setTimeout(r, typeWaitMs))
        charWidth += ctx.measureText(letter).width
        gif?.addFrame(canvas, { copy: true, delay: 5 })
      }
    } else {
      ctx.fillText(command.line, lineWidth, height - heightOffset)
    }

    gif?.addFrame(canvas, { copy: true, delay: 1 })

    if (command.timeout != 0) {
      await new Promise((r) => setTimeout(r, command.timeout))
      gif?.addFrame(canvas, { copy: true, delay: command.timeout })
    }

    if (isNewLine) {
      const imageData = ctx.getImageData(0, 0, width, height)
      ctx.clearRect(0, 0, width, height)
      ctx.putImageData(imageData, 0, -10 - fontSize)
    }
  }

  function fillHeader() {
    if (!ctx) {
      console.debug("Canvas doesn't exist")
      return
    }

    ctx.fillStyle = getColor(colors.header)
    ctx.fillRect(0, 0, width, 35)

    ctx.beginPath()
    ctx.arc(20, 19, 5, 0, 2 * Math.PI)
    ctx.fillStyle = getColor(colors.leftBtn)
    ctx.fill()

    ctx.beginPath()
    ctx.arc(40, 19, 5, 0, 2 * Math.PI)
    ctx.fillStyle = getColor(colors.rightBtn)
    ctx.fill()
  }

  function fillShStart(path) {
    if (!ctx) {
      console.debug("Canvas doesn't exist")
      return
    }

    if (path) {
      ctx.font = getFont(fontSize, fontFamily)
      ctx.fillStyle = getColor(colors.path)
      ctx.fillText(path, 20, height - heightOffset)
      ctx.fillText("$", ctx.measureText(path).width + 25, height - heightOffset)
    }
  }

  async function fillCommands() {
    for (let i = 0; i < $commandsData.length; i++) {
      const command = $commandsData[i]
      fillShStart(command.path)
      await renderCommandInCanvas(command, i < $commandsData.length - 1)
      fillHeader()
      if (command.timeout != 0) {
        await new Promise((r) => setTimeout(r, 200))
        gif?.addFrame(canvas, { copy: true, delay: 200 })
      }
    }
  }

  async function startAnimation() {
    if (isLoading || !ctx) return

    setLoading()
    ctx.font = getFont(fontSize, fontFamily)
    ctx.clearRect(0, 0, width, height)
    await new Promise((r) => setTimeout(r, 500))
    fillHeader()
    await fillCommands()
    setLoading({ val: false })
  }

  function downloadBlob(blob, name = "file.txt") {
    const blobUrl = URL.createObjectURL(blob)
    const link = document.createElement("a")

    link.href = blobUrl
    link.download = name
    document.body.appendChild(link)

    link.dispatchEvent(
      new MouseEvent("click", {
        bubbles: true,
        cancelable: true,
        view: window
      })
    )

    document.body.removeChild(link)
  }

  async function exportGif() {
    gif = new window.GIF({
      workers: 2,
      quality: 10
    })
    await startAnimation()
    setLoading({ elemName: "export" })

    gif.addFrame(canvas, { copy: true, delay: 1000 })

    gif.on("finished", (blob) => {
      setLoading({ val: false })
      downloadBlob(blob, "terminal.gif")
    })

    gif.render()
  }

  function setLoading(options = {}) {
    const { val = true, elemName = "simulate" } = options
    isLoading = val
    if (val) {
      loadingElem = elemName
      intervalInstance = setInterval(() => (dots += "."), 1000)
    } else {
      clearInterval(intervalInstance)
      dots = ""
    }
  }

  function updateData(data) {
    const { newCommands, newSettings, newColors } = data.detail
    let shouldOverwrite = true
    if ($commandsData.length >= 8) {
      shouldOverwrite = confirm("Are you sure you want to overwrite on existing commands?")
    }
    if (shouldOverwrite) {
      $commandsData = newCommands
      settings = newSettings
      Object.keys(newColors).forEach((colorKey) => (colors[colorKey].color = newColors[colorKey].color))
    }
  }
</script>

<svelte:head>
  <script src="/gif.js"></script>
  <script src="/gif.worker.js"></script>
</svelte:head>

<main>
  <div class="page-wrapper">
    <div class="page">
      <Header />
      <div class="flex justify-center">
        <canvas {width} {height} class="bg-slate-900 rounded-xl" />
      </div>
      <Buttons bind:isLoading bind:dots bind:loadingElem {settings} {startAnimation} {exportGif} {resetTerminal} {updateData} />

      <Commands />
      <ChangeLog />
      <Footer />
    </div>
  </div>
</main>

<style lang="postcss" global>
  @tailwind base;
  @tailwind components;
  @tailwind utilities;

  body,
  main {
    margin: 0;
    padding: 0;
  }

  button {
    @apply text-xs;
  }

  .timeout-input {
    @apply rounded-md border-none text-sm font-bold w-12 py-1 text-center outline-none bg-gray-100;
  }

  .command-input {
    @apply rounded-md bg-gray-100 border-none py-1 px-2 text-sm font-bold;
  }

  .app-btn {
    @apply py-2 px-4 rounded-md font-bold text-white border-none;
  }

  .page-wrapper {
    @apply flex flex-col w-full justify-center bg-gradient-to-t from-sky-300 to-lime-50;
  }

  .page {
    background: url(/images/footer-wave.svg) no-repeat;
    background-position: bottom 0 left;
  }

  @font-face {
    font-family: "Courier Prime Regular";
    font-weight: 900;
    src: url(/fonts/CourierPrime-Regular.ttf) format("truetype");
  }

  @font-face {
    font-family: "Courier Prime Bold";
    font-weight: 900;
    src: url(/fonts/CourierPrime-Bold.ttf) format("truetype");
  }

  @font-face {
    font-family: "Consolas Regular";
    font-weight: 900;
    src: url(/fonts/consolas-regular.ttf) format("truetype");
  }

  @font-face {
    font-family: "Consolas Bold";
    font-weight: 900;
    src: url(/fonts/consolas-bold.ttf) format("truetype");
  }
</style>
